/**
 * Nested panel shown when an attempt row is expanded: filter tabs (counted from the attempt's
 * action counters) + a server-paginated table of the attempt's actions in the active category.
 * Rows carrying before/after JSON expose a GitHub-style diff.
 */
import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { IStoreCopyAttempt } from '../../../../../services/api/troubleshooting-service';
import Pagination from '../../../../../components/table/components/pagination/pagination';
import LinearLoader from '../../../../../components/loaders/linear-loader/linear-loader';
import { ActionCategory, buildActionTabs } from '../action-categories';
import { AttemptActionsPagedListStore } from '../stores/attempt-actions-paged-list-store';
import ActionFilterTabs from './action-filter-tabs';
import JsonDiffView from './json-diff-view';

interface IAttemptActionsPanelProps {
    attempt: IStoreCopyAttempt;
}

const ACTION_LABELS: Record<string, string> = {
    created: 'Created',
    updated: 'Updated',
    integration_linked: 'Integration linked',
    integration_not_found: 'Integration not found',
    disabled: 'Disabled',
    soft_deleted: 'Soft deleted',
};

const AttemptActionsPanel: React.FC<IAttemptActionsPanelProps> = observer(({ attempt }) => {
    const [store] = useState(() => new AttemptActionsPagedListStore());
    const tabs = useMemo(() => buildActionTabs(attempt.action_counts), [attempt.action_counts]);
    const [active, setActive] = useState<ActionCategory>(
        // Default to "Requires attention" when it has items, otherwise "All".
        tabs.find((t) => t.id === 'requires_attention' && t.count > 0) ? 'requires_attention' : 'all',
    );

    useEffect(() => {
        if (attempt.actions_total > 0) {
            void store.open(attempt.id, active);
        }
        // Only (re)open for a different attempt; tab changes go through onTabChange.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attempt.id]);

    const onTabChange = (category: ActionCategory) => {
        setActive(category);
        void store.setCategory(category);
    };

    if (attempt.actions_total === 0) {
        return <p className="attempt-actions__empty">No actions were recorded for this attempt.</p>;
    }

    const { list: visibleActions, page, perPage, total, isLoading, error } = store;
    const activeCount = tabs.find((t) => t.id === active)?.count ?? 0;

    return (
        <div className="attempt-actions">
            <ActionFilterTabs tabs={tabs} active={active} onChange={onTabChange} />

            {isLoading && <LinearLoader />}
            {error && <p className="attempt-actions__error">{error}</p>}

            {activeCount === 0 ? (
                <p className="attempt-actions__empty">No actions in this category.</p>
            ) : (
                <div className="attempt-actions__table">
                    <div className="attempt-actions__row attempt-actions__row--head">
                        <span>Entity</span>
                        <span>Action</span>
                        <span>Source → New</span>
                        <span>Details</span>
                    </div>
                    {visibleActions.map((action) => {
                        const hasDiff = action.before_json != null || action.after_json != null;
                        return (
                            <div key={action.id} className="attempt-actions__row">
                                <span className="attempt-actions__entity">{action.entity_type}</span>
                                <span>
                                    <span className={`action-badge action-badge--${action.action}`}>
                                        {ACTION_LABELS[action.action] ?? action.action}
                                    </span>
                                </span>
                                <span className="attempt-actions__ids">
                                    {action.source_id ?? '—'} → {action.new_id ?? '—'}
                                </span>
                                <span className="attempt-actions__details">
                                    {action.message && (
                                        <p className="attempt-actions__message">{action.message}</p>
                                    )}
                                    {hasDiff && (
                                        <JsonDiffView beforeJson={action.before_json} afterJson={action.after_json} />
                                    )}
                                    {!action.message && !hasDiff && <span className="attempt-actions__muted">—</span>}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            {activeCount > 0 && (
                <Pagination
                    page={page}
                    perPage={perPage}
                    total={total}
                    totalResultsTitle="actions"
                    onSetPage={(nextPage) => {
                        void store.loadPage(nextPage);
                    }}
                />
            )}
        </div>
    );
});

export default AttemptActionsPanel;
